import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';

function buildD3Data(index, knownIds) {
  const uniprotsByNode = new Map();
  const root = { name: '__root__', children: new Map() };
  for (const [uid, paths] of Object.entries(index)) {
    if (knownIds && !knownIds.has(uid)) continue;
    for (const pathStr of paths) {
      const parts = pathStr.split(/\s*->\s*/).map(s => s.trim()).filter(Boolean);
      if (!parts.length) continue;
      let keyParts = [];
      let node = root;
      for (const part of parts) {
        keyParts = [...keyParts, part];
        const key = keyParts.join('|||');
        if (!uniprotsByNode.has(key)) uniprotsByNode.set(key, new Set());
        uniprotsByNode.get(key).add(uid);
        if (!node.children.has(part)) {
          node.children.set(part, { name: part, children: new Map() });
        }
        node = node.children.get(part);
      }
    }
  }
  const toPlain = (node, kp) => {
    const ch = Array.from(node.children.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, c]) => toPlain(c, [...kp, c.name]));
    return { name: node.name, key: kp.join('|||'), children: ch.length ? ch : undefined };
  };
  const children = Array.from(root.children.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, c]) => toPlain(c, [c.name]));
  return { d3Root: { name: 'MeSH', key: '', children }, uniprotsByNode };
}

export default function MeshSunburstFilter({ respiratoryOnly, onFilterChange, isDark, knownIds }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const zoomToRootRef = useRef(null);
  const updateSelRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [meshIdx, setMeshIdx] = useState(null);
  const [respIdx, setRespIdx] = useState(null);
  const [focusKey, setFocusKey] = useState(null);
  const [hoveredName, setHoveredName] = useState('');
  const focusKeyRef = useRef(null);
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${process.env.PUBLIC_URL}/data/mesh-index.json`).then(r => r.json()),
      fetch(`${process.env.PUBLIC_URL}/data/mesh-respiratory-index.json`).then(r => r.json()),
    ]).then(([mi, ri]) => {
      if (!cancelled) { setMeshIdx(mi); setRespIdx(ri); setLoading(false); }
    }).catch(err => {
      console.error('MeshSunburstFilter: failed to load MeSH data', err);
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const activeIndex = respiratoryOnly ? respIdx : meshIdx;

  const { d3Root, uniprotsByNode } = useMemo(() => {
    if (!activeIndex) return { d3Root: null, uniprotsByNode: new Map() };
    return buildD3Data(activeIndex, knownIds);
  }, [activeIndex, knownIds]);

  useEffect(() => {
    setFocusKey(null);
    setHoveredName('');
    focusKeyRef.current = null;
  }, [respiratoryOnly]);

  useEffect(() => {
    focusKeyRef.current = focusKey;
    onFilterChangeRef.current(focusKey ? (uniprotsByNode.get(focusKey) || new Set()) : null);
  }, [focusKey, uniprotsByNode]);

  // D3 rendering
  useEffect(() => {
    if (!d3Root || !svgRef.current || !containerRef.current) return;
    const container = containerRef.current;
    const svgEl = svgRef.current;
    // Square canvas — fills the container width
    const size = container.clientWidth || 560;
    const W = size;
    const H = size;
    const R = size / 2 * 0.88;
    const VIS_LAYERS = 2;

    d3.select(svgEl).selectAll('*').remove();
    const svg = d3.select(svgEl)
      .attr('viewBox', `0 0 ${W} ${H}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('display', 'block')
      .style('width', '100%')
      .style('height', 'auto');
    const g = svg.append('g').attr('transform', `translate(${W / 2},${H / 2})`);

    const hier = d3.hierarchy(d3Root).sum(() => 1);
    const maxDepth = Math.max(hier.height, 1);
    // Scale partition so exactly VIS_LAYERS rings fit within R at any zoom level
    d3.partition().size([2 * Math.PI, R * maxDepth / VIS_LAYERS])(hier);
    // Skip the MeSH root level: shift all y-values down by one ring step
    // so depth-1 categories become the innermost ring.
    const step = R / VIS_LAYERS;
    const cR = Math.max(R * 0.12, 18); // small centre nav button
    hier.each(d => {
      d.y0 = Math.max(0, d.y0 - step);
      d.y1 = Math.max(0, d.y1 - step);
      d.current = { x0: d.x0, x1: d.x1, y0: d.y0, y1: d.y1 };
    });

    const colorOrd = d3.scaleOrdinal(d3.schemeTableau10);
    const getColor = d => {
      if (!d.parent) return isDark ? '#3c3c3c' : '#eee';
      let top = d;
      while (top.depth > 1 && top.parent) top = top.parent;
      const base = d3.color(colorOrd(top.data.name));
      if (!base) return '#999';
      const bg = isDark ? '#252525' : '#fcfcfc';
      return d3.interpolateRgb(base.formatHex(), bg)(Math.min((d.depth - 1) * 0.22, 0.7));
    };

    const arcGen = d3.arc()
      .startAngle(d => d.x0)
      .endAngle(d => d.x1)
      .padAngle(d => Math.min((d.x1 - d.x0) / 2, 0.003))
      .padRadius(R / 2)
      .innerRadius(d => Math.max(cR, d.y0))
      .outerRadius(d => Math.max(d.y0, d.y1 - 1));

    const isVis = d => d.y1 <= R && d.y0 >= 0 && d.x1 > d.x0;
    const labelOk = d => {
      const span = d.x1 - d.x0;
      const mid = (Math.max(cR, d.y0) + d.y1) / 2;
      return d.y1 <= R && span * mid > 18;
    };
    const labelTf = d => {
      const mid = (d.x0 + d.x1) / 2; // radians, D3 convention (0 = top)
      const r = (Math.max(cR, d.y0) + d.y1) / 2;
      if (d.x1 - d.x0 > Math.PI) {
        // Arc spans more than half the circle — radial rotation would be near-vertical;
        // place text horizontally at the arc midpoint instead.
        return `translate(${r * Math.sin(mid)},${-r * Math.cos(mid)})`;
      }
      const deg = mid * 180 / Math.PI;
      return `rotate(${deg - 90}) translate(${r},0) rotate(${deg < 180 ? 0 : 180})`;
    };
    const wrapTextInArc = (sel, geom, name) => {
      const innerR = Math.max(cR, geom.y0);
      const arcLen = (geom.x1 - geom.x0) * (innerR + geom.y1) / 2;
      const radialH = geom.y1 - innerR;
      const lineH = 10; // px, for 9px font
      const maxLines = Math.max(1, Math.floor(radialH / lineH));
      const charsPerLine = Math.max(3, Math.floor(arcLen / 5.8));
      const words = name.split(' ');
      const lines = [];
      let cur = '';
      for (const w of words) {
        const test = cur ? cur + ' ' + w : w;
        if (test.length <= charsPerLine) {
          cur = test;
        } else {
          if (cur) lines.push(cur);
          cur = w.length > charsPerLine ? w.slice(0, charsPerLine - 1) + '…' : w;
        }
      }
      if (cur) lines.push(cur);
      const display = lines.slice(0, maxLines);
      const totalH = display.length * lineH;
      display.forEach((line, i) => {
        sel.append('tspan')
          .attr('x', 0)
          .attr('dy', i === 0 ? `${-(totalH / 2) + lineH * 0.82}px` : `${lineH}px`)
          .text(line);
      });
    };

    const paths = g.append('g').selectAll('path')
      .data(hier.descendants().slice(1))
      .join('path')
      .attr('fill', d => getColor(d))
      .attr('fill-opacity', d => isVis(d.current) ? (d.children ? 0.8 : 0.56) : 0)
      .attr('d', d => arcGen(d.current))
      .attr('pointer-events', d => isVis(d.current) ? 'auto' : 'none')
      .style('cursor', 'pointer');

    const labels = g.append('g')
      .attr('pointer-events', 'none')
      .attr('text-anchor', 'middle')
      .style('user-select', 'none')
      .selectAll('text')
      .data(hier.descendants().slice(1))
      .join('text')
      .attr('fill-opacity', d => +labelOk(d.current))
      .attr('transform', d => labelTf(d.current))
      .style('font-size', '9px')
      .style('fill', isDark ? '#eee' : '#111')
      .each(function(d) { wrapTextInArc(d3.select(this), d.current, d.data.name); });

    const selGroup = g.append('g').attr('pointer-events', 'none');
    const updateSel = key => {
      selGroup.selectAll('*').remove();
      if (!key) return;
      const nd = hier.descendants().find(d => d.data.key === key);
      if (!nd || !isVis(nd.current)) return;
      selGroup.append('path')
        .attr('d', arcGen(nd.current))
        .attr('fill', 'none')
        .attr('stroke', isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)')
        .attr('stroke-width', 2.5);
    };
    updateSelRef.current = updateSel;
    updateSel(focusKeyRef.current);

    const centerBg = g.append('circle')
      .datum(hier)
      .attr('r', cR)
      .attr('fill', isDark ? '#383838' : '#e4e4e4')
      .attr('stroke', isDark ? '#555' : '#bbb')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer');

    const centerTxt = g.append('text')
      .attr('text-anchor', 'middle')
      .style('pointer-events', 'none')
      .style('fill', isDark ? '#aaa' : '#666');

    const updateCenterLabel = (goesTo) => {
      centerTxt.selectAll('tspan').remove();
      centerTxt.text(null);
      if (!goesTo) {
        centerTxt.append('tspan').attr('x', 0).attr('dy', '0.35em').style('font-size', '13px').text('◉');
      } else {
        const name = goesTo.depth === 0 ? 'All' : goesTo.data.name;
        const maxChars = Math.max(4, Math.floor(cR * 1.4 / 5.5));
        const display = name.length > maxChars ? name.slice(0, maxChars - 1) + '…' : name;
        centerTxt.append('tspan').attr('x', 0).attr('dy', '-0.25em').style('font-size', '11px').text('↩');
        centerTxt.append('tspan').attr('x', 0).attr('dy', '1.1em').style('font-size', '8px').text(display);
      }
    };
    updateCenterLabel(null);

    const tip = d3.select(container).append('div')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('background', isDark ? 'rgba(22,22,22,0.93)' : 'rgba(255,255,255,0.96)')
      .style('border', `1px solid ${isDark ? '#555' : '#ccc'}`)
      .style('border-radius', '7px')
      .style('padding', '7px 10px')
      .style('font-size', '12px')
      .style('color', isDark ? '#eee' : '#222')
      .style('opacity', '0')
      .style('z-index', '200')
      .style('max-width', '240px')
      .style('word-break', 'break-word')
      .style('line-height', '1.45');

    paths
      .on('mouseover', (event, d) => {
        const cnt = uniprotsByNode.get(d.data.key)?.size ?? 0;
        tip.html(`<strong>${d.data.name}</strong><br/>${cnt} protein${cnt !== 1 ? 's' : ''}`).style('opacity', '1');
        setHoveredName(d.data.name);
      })
      .on('mousemove', event => {
        const rect = container.getBoundingClientRect();
        tip.style('left', `${Math.min(event.clientX - rect.left + 14, container.clientWidth - 260)}px`)
           .style('top', `${Math.max(event.clientY - rect.top - 46, 4)}px`);
      })
      .on('mouseout', () => { tip.style('opacity', '0'); setHoveredName(''); })
      .on('click', (e, d) => zoomTo(d));

    centerBg.on('click', (e, d) => zoomTo(d));

    function zoomTo(p) {
      const newKey = (p === hier || p.depth === 0) ? null : p.data.key;
      focusKeyRef.current = newKey;
      setFocusKey(newKey);
      updateSel(newKey);

      const parentNode = p.parent || hier;
      centerBg.datum(parentNode);
      updateCenterLabel(p.depth > 0 ? parentNode : null);

      hier.each(d => {
        const dx = Math.max(p.x1 - p.x0, 1e-9);
        d.target = {
          x0: Math.max(0, Math.min(1, (d.x0 - p.x0) / dx)) * 2 * Math.PI,
          x1: Math.max(0, Math.min(1, (d.x1 - p.x0) / dx)) * 2 * Math.PI,
          y0: Math.max(0, d.y0 - p.depth),
          y1: Math.max(0, d.y1 - p.depth),
        };
      });

      const t = g.transition().duration(650);

      paths.transition(t)
        .tween('data', d => {
          const interp = d3.interpolate(d.current, d.target);
          return t2 => { d.current = interp(t2); };
        })
        .filter(function(d) { return +this.getAttribute('fill-opacity') || isVis(d.target); })
        .attr('fill-opacity', d => isVis(d.target) ? (d.children ? 0.8 : 0.56) : 0)
        .attr('pointer-events', d => isVis(d.target) ? 'auto' : 'none')
        .attrTween('d', d => () => arcGen(d.current));

      labels.filter(function(d) { return +this.getAttribute('fill-opacity') || labelOk(d.target); })
        .transition(t)
        .attr('fill-opacity', d => +labelOk(d.target))
        .attrTween('transform', d => () => labelTf(d.current));

      t.on('end', () => updateSel(focusKeyRef.current));
    }

    zoomToRootRef.current = () => zoomTo(hier);

    return () => { tip.remove(); };
  }, [d3Root, isDark]);

  const focusCount = focusKey ? (uniprotsByNode.get(focusKey)?.size ?? 0) : null;

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 12, opacity: 0.7, fontSize: '0.9rem' }}>Loading MeSH data…</div>
  );
  if (!activeIndex) return (
    <div style={{ textAlign: 'center', padding: 12, opacity: 0.7, fontSize: '0.9rem' }}>MeSH data unavailable.</div>
  );

  return (
    <div ref={containerRef} className="mesh-d3-container" style={{ position: 'relative' }}>
      <div className="mesh-d3-infobar">
        {hoveredName
          ? <span className="mesh-d3-hover-name">{hoveredName}</span>
          : focusKey
            ? <span className="mesh-d3-selected-name">
                ◉ {focusKey.split('|||').pop()} — {focusCount} protein{focusCount !== 1 ? 's' : ''}
              </span>
            : <span className="mesh-d3-hint">Click a segment to filter · Click centre (↩) to zoom out</span>
        }
        {focusKey && (
          <button className="mesh-tree-reset" title="Clear filter"
            onClick={() => { setFocusKey(null); focusKeyRef.current = null; updateSelRef.current?.(null); zoomToRootRef.current?.(); }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, verticalAlign: 'middle' }}>autorenew</span>
          </button>
        )}
      </div>
      <svg ref={svgRef} />
    </div>
  );
}
